import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Search } from 'lucide-react';
import { PASSWORD_MIN_LENGTH, type Branch, type Role, type UserRow } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  FormError,
  PageHeader,
  Panel,
  Select,
  Spinner,
  Switch,
  TextInput,
  fieldErrors,
  formatRelative,
  useToast,
} from '../../ui';

export function StaffPage() {
  const { can } = useSession();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserRow[]>('/api/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/api/roles') });
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/api/branches') });
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);

  const branchName = useMemo(() => new Map((branches.data ?? []).map((b) => [b.id, b.name])), [branches.data]);
  const filtered = (users.data ?? []).filter((u) => {
    if (!showInactive && !u.isActive) return false;
    const s = search.trim().toLowerCase();
    return !s || u.fullName.toLowerCase().includes(s) || u.username.includes(s) || u.roleName.toLowerCase().includes(s);
  });
  const inactiveCount = (users.data ?? []).filter((u) => !u.isActive).length;
  const loading = users.isLoading || roles.isLoading || branches.isLoading;
  const error = users.error ?? roles.error ?? branches.error;

  return (
    <>
      <PageHeader
        title="Staff"
        description="Everyone who can sign in, what role they have and which branches they work at."
        actions={
          can('users.manage') && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')} disabled={loading}>
              Add staff
            </Button>
          )
        }
      />
      {error && <ErrorNotice error={error} onRetry={() => void users.refetch()} />}
      {loading && <Spinner />}
      {users.data && (
        <Panel>
          <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-3">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
              <TextInput placeholder="Search by name, username or role" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" aria-label="Search staff" />
            </div>
            {inactiveCount > 0 && <Checkbox label={`Show deactivated (${inactiveCount})`} checked={showInactive} onChange={setShowInactive} />}
          </div>
          {filtered.length === 0 ? (
            <EmptyState title={search ? 'No one matches that search' : 'No staff yet'}>{search ? 'Check the spelling or clear the search.' : 'Add the people who will sign in.'}</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2.5 font-medium">Name</th>
                    <th className="px-3 py-2.5 font-medium">Role</th>
                    <th className="px-3 py-2.5 font-medium">Branches</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-5 py-2.5 font-medium text-right">Last sign-in</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map((u) => (
                    <tr
                      key={u.id}
                      className={can('users.manage') ? 'cursor-pointer hover:bg-ledger-50/60' : undefined}
                      onClick={can('users.manage') ? () => setEditing(u) : undefined}
                    >
                      <td className="px-5 py-3">
                        {can('users.manage') ? (
                          <button className="text-left font-medium hover:underline" onClick={(e) => { e.stopPropagation(); setEditing(u); }}>
                            {u.fullName}
                          </button>
                        ) : (
                          <span className="font-medium">{u.fullName}</span>
                        )}
                        <span className="block text-sm text-muted">{u.username}</span>
                      </td>
                      <td className="px-3 py-3">{u.roleName}</td>
                      <td className="px-3 py-3 text-sm">{u.branchIds.map((id) => branchName.get(id)).filter(Boolean).join(', ')}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {!u.isActive ? <Badge>Deactivated</Badge> : u.isLocked ? <Badge tone="danger"><Lock className="mr-1 size-3" />Locked</Badge> : <Badge tone="ok">Active</Badge>}
                          {u.isActive && u.mustChangePassword && <Badge tone="gold">New password pending</Badge>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-sm text-muted num">{formatRelative(u.lastLoginAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
      {editing && roles.data && branches.data && (
        <StaffDialog user={editing === 'new' ? null : editing} roles={roles.data} branches={branches.data} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function StaffDialog({ user, roles, branches, onClose }: { user: UserRow | null; roles: Role[]; branches: Branch[]; onClose: () => void }) {
  const { me, can } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const isSelf = user?.id === me.user.id;
  const myPerms = new Set(me.user.permissions);
  const canAssign = (r: Role) => r.permissions.every((p) => myPerms.has(p));
  const activeBranches = branches.filter((b) => b.isActive || user?.branchIds.includes(b.id));

  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [roleId, setRoleId] = useState(user?.roleId ?? roles.find((r) => r.key === 'cashier')?.id ?? '');
  const [branchIds, setBranchIds] = useState<string[]>(user?.branchIds ?? (me.branches[0] ? [me.branches[0].id] : []));
  const [defaultBranchId, setDefault] = useState(user?.defaultBranchId ?? me.branches[0]?.id ?? '');
  const [isActive, setActive] = useState(user?.isActive ?? true);
  const [password, setPassword] = useState('');
  const [resetOpen, setResetOpen] = useState(false);

  const currentRole = roles.find((r) => r.id === user?.roleId);
  const readOnly = !!user && !isSelf && !!currentRole && !canAssign(currentRole);

  const save = useMutation({
    mutationFn: () => {
      const body = { fullName, email, phone, roleId, branchIds, defaultBranchId: branchIds.includes(defaultBranchId) ? defaultBranchId : (branchIds[0] ?? '') };
      return user ? api.put<UserRow>(`/api/users/${user.id}`, { ...body, isActive }) : api.post<UserRow>('/api/users', { ...body, username, password });
    },
    onSuccess: () => {
      toast(user ? 'Staff details saved' : `Account created. ${fullName.split(' ')[0]} will choose a new password at first sign-in.`);
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      if (isSelf) void qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
    },
  });
  const action = useMutation({
    mutationFn: (kind: 'unlock' | 'sign-out') => api.post<{ revoked?: number }>(`/api/users/${user!.id}/${kind}`),
    onSuccess: (res, kind) => {
      toast(kind === 'unlock' ? 'Account unlocked' : `Signed out of ${res.revoked ?? 0} device(s)`);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const f = fieldErrors(save.error);

  const toggleBranch = (id: string, on: boolean) => setBranchIds((ids) => (on ? [...new Set([...ids, id])] : ids.filter((x) => x !== id)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={user ? user.fullName : 'Add staff'}
      description={user ? `Username: ${user.username}` : 'They will sign in with this username and the password you set, then choose their own.'}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="staff-form" busy={save.isPending} disabled={readOnly}>
            {user ? 'Save changes' : 'Create account'}
          </Button>
        </>
      }
    >
      {readOnly && (
        <p className="mb-4 rounded-[8px] bg-gold-soft px-3 py-2 text-sm text-gold-ink">
          This person's role ({currentRole?.name}) has permissions you don't have, so only someone with those permissions can change their account.
        </p>
      )}
      <FormError error={save.error && !Object.keys(f).length ? save.error : null} />
      <form id="staff-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <fieldset disabled={readOnly} className="contents">
          <Field label="Full name" htmlFor="fullName" error={f.fullName}>
            <TextInput id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} invalid={!!f.fullName} autoFocus />
          </Field>
          {!user ? (
            <Field label="Username" htmlFor="username" error={f.username} hint="Lowercase, e.g. farai.ncube">
              <TextInput id="username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} autoCapitalize="none" spellCheck={false} invalid={!!f.username} />
            </Field>
          ) : (
            <div className="hidden sm:block" />
          )}
          <Field label="Phone" htmlFor="phone" error={f.phone}>
            <TextInput id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+263 77 123 4567" invalid={!!f.phone} />
          </Field>
          <Field label="Email (optional)" htmlFor="email" error={f.email}>
            <TextInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} invalid={!!f.email} />
          </Field>
          <Field label="Role" htmlFor="role" error={f.roleId} hint={isSelf ? "You can't change your own role." : undefined}>
            <Select id="role" value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={isSelf} invalid={!!f.roleId}>
              {roles.map((r) => (
                <option key={r.id} value={r.id} disabled={!canAssign(r) && r.id !== user?.roleId}>
                  {r.name}
                  {!canAssign(r) ? ' (needs permissions you don’t have)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          {!user && (
            <Field label="Starting password" htmlFor="password" error={f.password} hint={`At least ${PASSWORD_MIN_LENGTH} characters with a letter and a number. Share it privately.`}>
              <TextInput id="password" type="text" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} invalid={!!f.password} />
            </Field>
          )}
          <div className="sm:col-span-2">
            <p className="mb-2 text-sm font-medium">Branches</p>
            <div className="grid gap-2 rounded-[8px] border border-line p-3 sm:grid-cols-2">
              {activeBranches.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-2">
                  <Checkbox label={b.name} description={b.code} checked={branchIds.includes(b.id)} onChange={(v) => toggleBranch(b.id, v)} />
                  {branchIds.includes(b.id) && branchIds.length > 1 && (
                    <label className="flex items-center gap-1.5 text-sm text-muted">
                      <input type="radio" name="default-branch" checked={defaultBranchId === b.id} onChange={() => setDefault(b.id)} className="accent-ledger-600" />
                      Default
                    </label>
                  )}
                </div>
              ))}
            </div>
            {(f.branchIds || f.defaultBranchId) && <p className="mt-1 text-sm text-danger">{f.branchIds ?? f.defaultBranchId}</p>}
          </div>
          {user && !isSelf && (
            <div className="flex items-center justify-between gap-4 rounded-[8px] bg-paper px-4 py-3 sm:col-span-2">
              <div>
                <p className="font-medium">Account active</p>
                <p className="text-sm text-muted">Deactivating signs them out everywhere and stops them signing in. Their history is kept.</p>
              </div>
              <Switch checked={isActive} onChange={setActive} label="Account active" />
            </div>
          )}
        </fieldset>
      </form>

      {user && !isSelf && !readOnly && can('users.manage') && (
        <div className="mt-6 border-t border-line pt-5">
          <p className="mb-3 text-sm font-medium">Account security</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setResetOpen(true)}>
              Reset password
            </Button>
            {user.isLocked && (
              <Button size="sm" onClick={() => action.mutate('unlock')} busy={action.isPending && action.variables === 'unlock'}>
                Unlock account
              </Button>
            )}
            <Button size="sm" onClick={() => action.mutate('sign-out')} busy={action.isPending && action.variables === 'sign-out'}>
              Sign out of all devices
            </Button>
          </div>
          {action.error && <p className="mt-2 text-sm text-danger">{(action.error as Error).message}</p>}
        </div>
      )}
      {resetOpen && user && <ResetPasswordDialog user={user} onClose={() => setResetOpen(false)} />}
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const toast = useToast();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post(`/api/users/${user.id}/reset-password`, { password }),
    onSuccess: () => {
      toast(`Password reset. ${user.fullName.split(' ')[0]} must choose a new one at next sign-in.`);
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reset password for ${user.fullName}`}
      description="They will be signed out everywhere, any lock is cleared, and they must choose their own password next time."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => m.mutate()} busy={m.isPending}>
            Reset password
          </Button>
        </>
      }
    >
      <FormError error={m.error && !Object.keys(f).length ? m.error : null} />
      <Field label="Temporary password" htmlFor="tmp" error={f.password} hint={`At least ${PASSWORD_MIN_LENGTH} characters with a letter and a number.`}>
        <TextInput id="tmp" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} invalid={!!f.password} autoFocus />
      </Field>
    </Dialog>
  );
}
